import { useSyncExternalStore } from "react";
import type { FeedbackType } from "@shared/feedbackBoard";

/**
 * App-wide state for the single Bugs & Features floating panel.
 *
 * Several controls can ask to open it (the floating FeedbackButton, the
 * /feedback page button, the mobile FAB item), but only FeedbackButton renders
 * the panel, from this store, so at most one panel ever exists. Opening while
 * already open is a no-op, so an opener never resets the panel or its draft.
 *
 * The draft (typed fields + picked screenshots), the collapsed state and the
 * position all live here rather than in the panel's component state, so they
 * survive the cross-route remount that happens when navigation unmounts the
 * owning FeedbackButton and the next page mounts a fresh one while the panel is
 * still open. The draft is discarded — and screenshot preview URLs revoked —
 * only when the panel actually closes (X / Esc / successful submit); navigation
 * keeps it.
 */

export interface PanelPosition {
  left: number;
  top: number;
}

export interface PickedScreenshot {
  key: number;
  file: File;
  /** Object URL for the preview; owned by the store and revoked on remove / close, never on unmount. */
  previewUrl: string;
}

export interface FeedbackDraft {
  type: FeedbackType;
  title: string;
  steps: string;
  expectedVsActual: string;
  description: string;
  screenshots: PickedScreenshot[];
}

export interface FeedbackPanelState {
  open: boolean;
  collapsed: boolean;
  /** null until the first layout pass measures the panel and parks it bottom-right. */
  position: PanelPosition | null;
  draft: FeedbackDraft;
  /**
   * True from the moment a submit starts until the request settles. Lives here, not in the form, so
   * the Send button stays disabled across a cross-route remount and the submit can complete even
   * after the form that started it unmounts.
   */
  submitting: boolean;
  /** Message of the last failed submit, shown inline; cleared when a new submit starts or the panel resets. */
  submitError: string | null;
}

function makeBlankDraft(): FeedbackDraft {
  return { type: "bug", title: "", steps: "", expectedVsActual: "", description: "", screenshots: [] };
}

function makeClosedState(): FeedbackPanelState {
  return { open: false, collapsed: false, position: null, draft: makeBlankDraft(), submitting: false, submitError: null };
}

let state: FeedbackPanelState = makeClosedState();
let nextKey = 0;
/**
 * Bumped whenever a submit starts and whenever the panel is reset/closed (every place
 * makeClosedState() is assigned). A submit captures the value at start; if it no longer matches
 * when the request settles, the panel has since been closed and/or a new draft started, so the
 * settled submit must not touch the store (it would wipe the new draft or write a stale error).
 */
let submitGeneration = 0;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function revokeScreenshots() {
  state.draft.screenshots.forEach((shot) => URL.revokeObjectURL(shot.previewUrl));
}

export function openFeedbackPanel() {
  if (state.open) return; // keep the existing panel (and its draft), don't reset it
  state = { ...state, open: true };
  emit();
}

/** Clear + revoke back to the closed state and invalidate any in-flight submit. Does not emit. */
function resetToClosed() {
  revokeScreenshots();
  state = makeClosedState();
  nextKey = 0;
  submitGeneration++;
}

export function closeFeedbackPanel() {
  if (!state.open) return;
  // Closing discards the draft; free the preview URLs it still held so nothing leaks.
  resetToClosed();
  emit();
}

/** Hard reset (clear + revoke) regardless of open state — for test setup. */
export function resetFeedbackPanel() {
  resetToClosed();
  emit();
}

export function setFeedbackCollapsed(next: boolean | ((prev: boolean) => boolean)) {
  const collapsed = typeof next === "function" ? next(state.collapsed) : next;
  if (collapsed === state.collapsed) return;
  state = { ...state, collapsed };
  emit();
}

export function setFeedbackPosition(
  next: PanelPosition | null | ((prev: PanelPosition | null) => PanelPosition | null),
) {
  const position = typeof next === "function" ? next(state.position) : next;
  if (position === state.position) return;
  state = { ...state, position };
  emit();
}

export function updateFeedbackDraft(patch: Partial<Omit<FeedbackDraft, "screenshots">>) {
  // Editing any field clears the last submit error — the input the user is changing is the retry.
  state = { ...state, draft: { ...state.draft, ...patch }, submitError: null };
  emit();
}

/** Keep the File objects in the store; previews stay valid across remounts because we never revoke on unmount. */
export function appendFeedbackScreenshots(files: File[]) {
  if (files.length === 0) return;
  const added = files.map((file) => ({ key: nextKey++, file, previewUrl: URL.createObjectURL(file) }));
  state = { ...state, draft: { ...state.draft, screenshots: [...state.draft.screenshots, ...added] } };
  emit();
}

export function removeFeedbackScreenshot(key: number) {
  const target = state.draft.screenshots.find((shot) => shot.key === key);
  if (!target) return;
  URL.revokeObjectURL(target.previewUrl);
  state = { ...state, draft: { ...state.draft, screenshots: state.draft.screenshots.filter((shot) => shot.key !== key) } };
  emit();
}

/**
 * Run a feedback submit through the store so completion is independent of the form's lifetime.
 *
 * `request` performs the POST (and reads the response). `onSent` runs the "it was delivered"
 * effects (success toast, query invalidation): the report truly was sent, so these fire on every
 * success even if the panel has since been closed and a new draft started. `onDone` closes the
 * panel and only runs when the submit is still the current one. Both capture stable references
 * (the app queryClient, the module `toast`), so they are safe to call after the form unmounts. A
 * second call while a submit is in flight is ignored.
 *
 * A submit captures `submitGeneration` at start; every reset/close bumps it. When the request
 * settles we compare: if the generation moved on, the panel was closed (and maybe reopened with a
 * fresh draft) in the meantime, so we must not touch the store — clearing it would wipe the new
 * draft and close the panel, and recording an error would surface it on the closed panel's next
 * open. We only run `onSent` (delivery already happened) and stop.
 *
 * When still current: on success we clear the draft and close the panel exactly like
 * `closeFeedbackPanel` (revoke preview URLs, reset state) so a remounted form can't show the
 * still-intact draft and re-send, then run `onDone`; on failure we clear `submitting` and record
 * the error, leaving the draft so the user can retry.
 */
export async function submitFeedback(
  request: () => Promise<unknown>,
  onSent: () => void,
  onDone: () => void,
): Promise<void> {
  if (state.submitting) return;
  const generation = ++submitGeneration;
  state = { ...state, submitting: true, submitError: null };
  emit();
  try {
    await request();
  } catch (error) {
    // Stale (panel closed/reopened mid-flight): don't write an error onto the current state.
    if (generation !== submitGeneration) return;
    const submitError = error instanceof Error ? error.message : "Something went wrong. Please try again.";
    state = { ...state, submitting: false, submitError };
    emit();
    return;
  }
  // The report was sent — always run the delivery effects (toast + invalidate).
  onSent();
  // Stale: leave the current panel/draft alone, just report delivery above.
  if (generation !== submitGeneration) return;
  resetToClosed();
  emit();
  onDone();
}

export function useFeedbackPanelState(): FeedbackPanelState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

export function useFeedbackPanelOpen(): boolean {
  return useSyncExternalStore(subscribe, () => state.open, () => state.open);
}
